import { isMobileOnly, isTablet } from 'react-device-detect';
import { APP_INFO, FORM_SUBMIT_STATUS } from '../constants/form-constants';
import { formService } from '../form-service';
import { externalContentManager } from '../service/external-content';
import { strCapitalize } from '../utils/string-utils';
import { isEmptyValue } from '../utils/value-utils';

export const formDataFilter = (formData: Record<string, any>): Record<string, any> => {
	const filtered: Record<string, any> = {};

	for (const key in formData) {
		const value = formData[key];

		if (isEmptyValue(value)) {
			continue;
		}

		if (Array.isArray(value) || typeof value === 'object') {
			continue;
		}

		filtered[key] = String(value);
	}

	return filtered;
};

export const formDataBuilder = (formData: Record<string, any>): FormData => {
	const data = new FormData();

	for (const key in formData) {
		const value = formData[key];
		data.append(key, value);
	}

	return data;
};

export const isLayerForm = (): boolean => {
	return externalContentManager.isLayerForm;
};

export const scrollToTop = (): void => {
	if (isLayerForm()) return;
	window.scrollTo({ top: 0, behavior: 'smooth' });
};

export const isInvertedThemeForm = (): boolean => {
	return externalContentManager.isInvertedThemeForm;
};

// NOT IN USE
export const isFormSubmitInvalid = formService.formSubmitStatus() == FORM_SUBMIT_STATUS.INVALID;
export const isFormSubmitSending = formService.formSubmitStatus() == FORM_SUBMIT_STATUS.SENDING;
export const isFormSubmitSuccess = formService.formSubmitStatus() == FORM_SUBMIT_STATUS.SUCCESS;
export const isFormSubmitError = formService.formSubmitStatus() == FORM_SUBMIT_STATUS.ERROR;

declare global {
	interface Window {
		s?: {
			persCmp?: string;
		};
	}
}

export const getParamsForm = () => {
	let tc: string;
	if (typeof (window as any).s !== 'undefined') {
		if (typeof (window as any).s.persCmp !== 'undefined') {
			tc = (window as any).s.persCmp;
		} else {
			tc = '';
		}
	} else {
		tc = 'undefined';
	}

	const date = new Date();
	const device = isMobileOnly ? 'mobile' : isTablet ? 'tablet' : 'desktop';
	const location = window.location.href;
	const referalUrl = document.referrer.substring(0, 30);
	const today = date.toISOString().replace('T', ' ').split('.')[0];
	const url = window.location.origin + window.location.pathname;

	return {
		device,
		location,
		referalUrl,
		tc,
		today,
		url,
	};
};

// Ejemplo: 			lead_venta-mas_informacion
// Ejemplo addPrefix: 	vw-oneform-lead_venta-mas_informacion
export const getFormName = (addPrefix?: boolean): string => {
	let formName = externalContentManager.formName;

	if (formName) {
		if (fromOfertasFeatureApp()) {
			formName += '-oferta';
		}
		if (fromConfiguradorFeatureApp()) {
			formName += '-configure';
		}
		if (fromDealerFeatureApp()) {
			formName += '-dealer';
		}
		return (addPrefix ? `${APP_INFO.NAME}-` : '') + formName;
	}

	return 'no-form-detected';
};

// Ejemplo: 			lead_venta-mas_informacion-3.7.0-release.001
// Ejemplo addPrefix: 	vw-oneform-lead_venta-mas_informacion-3.7.0-release.001
export const getFormVersion = (addPrefix?: boolean): string => {
	const formName = getFormName(addPrefix);
	// @ts-ignore
	return `${formName}-${process.env.VERSION}`;
};

export const getFormType = (): string => {
	if (isLeadVentaPresupuestoForm()) {
		return 'quote';
	}
	if (isLeadVentaTestDriveForm()) {
		return 'testdrive';
	}
	return 'contact';
};

export const getPageCategory = (): string => {
	const formName = getFormName();
	return strCapitalize(formName?.split('-')[1]);
};

export const isTypeForm = (type: string): boolean => {
	const formNames = externalContentManager.forms;
	if (formNames && formNames.some((name) => name.toLowerCase().startsWith(type.toLowerCase()))) {
		return true;
	}

	return externalContentManager.formName?.toLowerCase().startsWith(type.toLowerCase());
};

export const isSubTypeForm = (subType: string): boolean => {
	const formNames = externalContentManager.forms;
	if (formNames && formNames.some((name) => name.toLowerCase() == subType.toLowerCase())) {
		return true;
	}

	return externalContentManager.formName?.toLowerCase() == subType.toLowerCase();
};

export const isLeadVentaForm = (): boolean => {
	return isTypeForm('lead_venta');
};
export const isLeadVentaMasInformacionForm = (): boolean => {
	return isSubTypeForm('lead_venta-mas_informacion');
};
export const isLeadVentaTestDriveForm = (): boolean => {
	return isSubTypeForm('lead_venta-test_drive');
};
export const isLeadVentaPresupuestoForm = (): boolean => {
	return isSubTypeForm('lead_venta-presupuesto');
};
export const isLeadVentaVehiculoOcasionForm = (): boolean => {
	return isSubTypeForm('lead_venta-vo');
};
export const isLeadVentaStockForm = (): boolean => {
	return isSubTypeForm('lead_venta-stock');
};
export const isLeadVentaCitaForm = (): boolean => {
	return isSubTypeForm('lead_venta-cita');
};
export const isCallMeForm = (): boolean => {
	return isSubTypeForm('lead_venta-call_me');
};
export const isCallCenterForm = (): boolean => {
	return isSubTypeForm('lead_venta-call_center');
};
export const isSolucionesEstandarizadasForm = (): boolean => {
	return isSubTypeForm('lead_venta-soluciones_estandarizadas');
};

export const fromOfertasFeatureApp = (): boolean => {
	return externalContentManager.featureApp == 'esofertasv2';
};
export const fromConfiguradorFeatureApp = (): boolean => {
	return externalContentManager.featureApp == 'configure';
};
export const fromDealerFeatureApp = (): boolean => {
	return externalContentManager.featureApp == 'dealer';
};

export const hasOfertaFromExternalContent = (): boolean => {
	return !isEmptyValue(externalContentManager.ofertaId);
};
export const hasMarketingCodeFromExternalContent = (): boolean => {
	return !isEmptyValue(externalContentManager.marketingCode);
};
export const hasSalesgroupFromExternalContent = (): boolean => {
	return (
		!isEmptyValue(externalContentManager.salesgroups) || !isEmptyValue(externalContentManager.token?.salesgroupCode)
	);
};
export const hasCarlineFromExternalContent = (): boolean => {
	return !isEmptyValue(externalContentManager.carlines) || !isEmptyValue(externalContentManager.token?.carlineCode);
};
